import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, IconButton } from '../primitives';
import type { IconName } from '../icons';
import { actionLabel } from './actionLabels';
import { impactWords, type ImpactAssessment, type ImpactLevel } from './impact';

/**
 * The shared pieces of an operation's summary (UX_ARCHITECTURE.md, levels 1
 * and 2): one conclusion, the impact, the facts that explain it, and what to
 * do next. Deployments, backups, restores, scans and sync jobs compose the
 * same pieces with their own facts; none of them is a template with flags.
 */

export type SummaryTone = 'danger' | 'warning' | 'success' | 'progress' | 'neutral';

const toneIcon: Record<SummaryTone, IconName> = {
  danger: 'alert-circle',
  warning: 'alert-triangle',
  success: 'check-circle',
  progress: 'refresh',
  neutral: 'info',
};

/**
 * The surface of a summary: the page's own colour with a toned edge and a
 * toned icon, never a slab of red or green. The conclusion is its heading.
 */
export function OperationSummary({
  tone,
  title,
  badge,
  where,
  actions,
  children,
  testId,
  live = false,
}: {
  tone: SummaryTone;
  /** The one conclusion of the screen, in a few words: "Deployment failed". */
  title: ReactNode;
  /** A compact label beside the conclusion: the impact. */
  badge?: ReactNode;
  /** Only when a line under the conclusion says something the fields do not. */
  where?: ReactNode;
  /** Evidence actions: View error, Full logs. */
  actions?: ReactNode;
  children?: ReactNode;
  testId?: string;
  /** Announce a change of the conclusion politely (a running operation). */
  live?: boolean;
}) {
  return (
    <section className={`op-summary op-summary--${tone}`} aria-labelledby={testId ? `${testId}-title` : undefined} data-testid={testId}>
      <div className="op-summary__head">
        <span className="op-summary__icon" aria-hidden="true">
          <Icon name={toneIcon[tone]} size={20} className={tone === 'progress' ? 'co-spin-slow' : undefined} />
        </span>
        <div className="op-summary__heading" aria-live={live ? 'polite' : undefined}>
          <h3 id={testId ? `${testId}-title` : undefined} className="op-summary__title">
            {title}
            {badge ? <span className="op-summary__badge">{badge}</span> : null}
          </h3>
          {where ? <p className="op-summary__where">{where}</p> : null}
        </div>
        {actions ? <div className="op-summary__actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** Rows of "what / answer", the same on every operation. */
export function SummaryFacts({ children }: { children: ReactNode }) {
  return <dl className="op-facts">{children}</dl>;
}

export function SummaryFact({ label, children, testId, wide = false }: { label: string; children: ReactNode; testId?: string; wide?: boolean }) {
  return (
    <div className={`op-facts__row${wide ? ' op-facts__row--wide' : ''}`}>
      <dt>{label}</dt>
      <dd data-testid={testId}>{children}</dd>
    </div>
  );
}

const impactIcon: Record<ImpactLevel, IconName> = {
  none_observed: 'check-circle',
  detected: 'alert-circle',
  potential: 'alert-triangle',
  not_verified: 'info',
};

/**
 * The impact of an operation on production, as a compact label: an icon and
 * the level in words, so it never rests on colour alone. The sentence that
 * says why is its tooltip and its accessible description; the visible
 * evidence is the fields beside it (traffic, database).
 */
export function ImpactLabel({ impact, testId }: { impact: ImpactAssessment; testId?: string }) {
  return (
    <span className={`impact-label impact-label--${impact.level}`} data-testid={testId} data-impact={impact.level} title={impact.summary}>
      <Icon name={impactIcon[impact.level]} size={14} className="impact-label__icon" />
      {impactWords[impact.level].label}
      <span className="visually-hidden">. {impact.summary}</span>
    </span>
  );
}

export interface NextStep {
  label: string;
  detail?: string;
  /** An in-app route. */
  to?: string;
  /** A command to run on the server: shown to copy, never run by the UI. */
  command?: string;
}

/**
 * What to do next. Every step comes from the server's interpretation; when
 * there is none, the fallback says that the cause is not identified and
 * names the evidence to read — it never invents a step.
 */
export function NextSteps({ steps, unknown, testId }: { steps: NextStep[]; unknown?: ReactNode; testId?: string }) {
  if (steps.length === 0) return <div data-testid={testId}>{unknown ?? 'No specific step is known.'}</div>;
  return (
    <ol className="op-steps" data-testid={testId}>
      {steps.map((step, index) => (
        <li key={`${step.label}-${index}`}>
          {step.to ? <Link to={step.to}>{step.label}</Link> : <span>{step.label}</span>}
          {step.detail && step.detail !== step.label ? <span className="muted"> {step.detail}</span> : null}
          {step.command ? (
            <span className="op-steps__command">
              <code translate="no">{step.command}</code>
              <IconButton
                icon="copy"
                label="Copy the command"
                onClick={() => {
                  void navigator.clipboard?.writeText(step.command!);
                }}
              />
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/** The two evidence buttons every failing operation offers, in the shared words. */
export function EvidenceActions({ onViewError, onFullLogs, errorTestId, logsTestId }: { onViewError?: () => void; onFullLogs?: () => void; errorTestId?: string; logsTestId?: string }) {
  return (
    <>
      {onViewError ? (
        <button type="button" className="button button--secondary button--small" onClick={onViewError} data-testid={errorTestId}>
          {actionLabel.viewError}
        </button>
      ) : null}
      {onFullLogs ? (
        <button type="button" className="button button--ghost button--small" onClick={onFullLogs} data-testid={logsTestId}>
          {actionLabel.fullLogs}
        </button>
      ) : null}
    </>
  );
}
